import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Modal from "react-modal";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import {
  FaArrowRight,
  FaPlay,
  FaXmark,
  FaRegFolderOpen,
} from "react-icons/fa6";
import { useAboutContext } from "../../context/About_context";
import Loader from "../../Common/loader/Loader";
import No_img from "../../Assets/No_img.jpg";
import {
  renderTitleWithGradient,
  getEmbedUrl,
  getYouTubeThumbnail,
} from "../../utils/helper";
import "./NewsLetterItemPage.css";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const parseTags = (rawTags) => {
  if (Array.isArray(rawTags)) return rawTags;
  if (typeof rawTags === "string" && rawTags.trim()) {
    return rawTags
      .split(",")
      .map((t) => t.trim().replace(/^["']+|["']+$/g, ""))
      .filter(Boolean);
  }
  return [];
};

const NewsLetterItemPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const categoryId = id || location.state?.categoryId;

  const {
    newsletter_items_loading,
    newsletter_items_data,
    GetNewsletterItems,
  } = useAboutContext();

  const [selectedSubCatId, setSelectedSubCatId] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef(null);

  // Initial Fetch
  useEffect(() => {
    if (categoryId) {
      GetNewsletterItems(categoryId);
      setSelectedSubCatId("all");
      setSelectedItem(null);
    }
  }, [categoryId]); // eslint-disable-line

  const category = newsletter_items_data?.category;
  const subcategories = Array.isArray(newsletter_items_data?.subcategories)
    ? newsletter_items_data.subcategories
        .filter(
          (sc) => sc.status === 1 || sc.status === "1" || sc.status === undefined,
        )
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
    : [];

  // Subcategory Change
  const handleSubCatChange = (subCatId, slideIndex) => {
    setSelectedSubCatId(subCatId);
    setSelectedItem(null);

    if (swiperRef.current && typeof slideIndex === "number") {
      swiperRef.current.slideTo(Math.max(0, slideIndex - 1), 350);
    }

    if (subCatId === "all") {
      GetNewsletterItems(categoryId);
    } else {
      GetNewsletterItems(categoryId, subCatId);
    }
  };

  const items = Array.isArray(newsletter_items_data?.items)
    ? newsletter_items_data.items
        .filter(
          (item) =>
            item.status === 1 || item.status === "1" || item.status === undefined,
        )
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
    : [];

  if (
    newsletter_items_loading ||
    !newsletter_items_data ||
    Object.keys(newsletter_items_data).length === 0 ||
    String(newsletter_items_data?.category?.id) !== String(categoryId)
  ) {
    return <Loader fullScreen={true} text="Loading Items..." />;
  }

  return (
    <div className="newsletter-item-page">
      <Helmet>
        <title>
          {category?.name ? `${category.name} | Bharat Buildcon` : "Newsletter Items"}
        </title>
        {category?.desc && <meta name="description" content={category.desc} />}
      </Helmet>

      {/* Ambient Background Glows */}
      <div className="nli-ambient-glow"></div>
      <div className="nli-ambient-glow-secondary"></div>

      <div className="nli-main-container">
        {/* ==================== HEADER SECTION ==================== */}
        <section className="nli-header-container">
          <div className="clean-breadcrumb-wrapper">
            <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="bc-home-link">
                HOME
              </Link>
              <span className="bc-slash">/</span>
              <Link to="/newsletter" className="bc-home-link">
                NEWSLETTERS
              </Link>
              {category?.name && (
                <>
                  <span className="bc-slash">/</span>
                  <span className="bc-active-page">{category.name}</span>
                </>
              )}
            </nav>
          </div>

          {category?.name && (
            <h1 className="nli-main-title">
              {category.name.includes(" ") ? (
                renderTitleWithGradient(category.name, "gradient-text")
              ) : (
                renderTitleWithGradient(category.name, "gradient-text")
              )}
            </h1>
          )}

          {category?.desc && (
            <p className="nli-subtitle-text">{category.desc}</p>
          )}
        </section>

        {/* ==================== SUBCATEGORY FILTER TABS ==================== */}
        {subcategories.length > 0 && (
          <div className="nli-tabs-container">
            <div
              className={`nli-tabs-wrapper ${!isBeginning ? "fade-left" : ""} ${!isEnd ? "fade-right" : ""}`}
            >
              <Swiper
                modules={[FreeMode, Mousewheel]}
                slidesPerView="auto"
                spaceBetween={6}
                resistance={true}
                resistanceRatio={0}
                watchOverflow={true}
                centerInsufficientSlides={true}
                freeMode={{
                  enabled: true,
                  momentum: true,
                  momentumRatio: 0.8,
                  momentumBounce: false,
                }}
                mousewheel={{
                  forceToAxis: true,
                  sensitivity: 1,
                  releaseOnEdges: true,
                }}
                grabCursor={true}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  setIsBeginning(swiper.isBeginning);
                  setIsEnd(swiper.isEnd);
                }}
                onProgress={(swiper) => {
                  setIsBeginning(swiper.isBeginning);
                  setIsEnd(swiper.isEnd);
                }}
                className="nli-tabs-swiper"
              >
                <SwiperSlide className="nli-tab-slide">
                  <button
                    type="button"
                    className={`nli-tab-btn ${selectedSubCatId === "all" ? "active" : ""}`}
                    onClick={() => handleSubCatChange("all", 0)}
                  >
                    <span className="nli-tab-dot" />
                    <span className="nli-tab-text">All</span>
                  </button>
                </SwiperSlide>
                {subcategories.map((subCat, idx) => (
                  <SwiperSlide key={subCat.id} className="nli-tab-slide">
                    <button
                      type="button"
                      className={`nli-tab-btn ${selectedSubCatId === subCat.id ? "active" : ""}`}
                      onClick={() => handleSubCatChange(subCat.id, idx + 1)}
                    >
                      <span className="nli-tab-dot" />
                      <span className="nli-tab-text">{subCat.name}</span>
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}

        {/* ==================== ITEMS GRID ==================== */}
        {items.length > 0 ? (
          <section className="nli-grid-section" aria-label="Category Items">
            <div className="nli-cards-grid">
              {items.map((item, index) => {
                const itemId = item.id || `item-${index}`;
                const itemTags = parseTags(item.tags);
                const isVideo =
                  item.media_type === "video_link" ||
                  (Boolean(item.video_link) && !item.image_full_path);
                const displayImg =
                  item.image_full_path ||
                  getYouTubeThumbnail(item.video_link) ||
                  No_img;
                const buttonText =
                  item.button_text || (isVideo ? "Watch" : "Read More");

                return (
                  <div key={itemId} className="nli-card-slot">
                    <article
                      className="nli-item-card"
                      onClick={() => setSelectedItem(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedItem(item);
                        }
                      }}
                    >
                      {/* Media Header - Completely clean, NO tags on image */}
                      <div className="nli-card-media-box">
                        <img
                          src={displayImg}
                          alt={item.title || ""}
                          className="nli-card-media-img"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = No_img;
                          }}
                        />

                        {/* Play Button Overlay for Videos */}
                        {isVideo && (
                          <div className="nli-play-overlay-static">
                            <span className="nli-play-pulse-ring" />
                            <div className="nli-play-icon-circle">
                              <FaPlay className="nli-play-triangle" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Card Content Body */}
                      <div className="nli-card-body">
                        {/* Title */}
                        {item.title && (
                          <h3 className="nli-card-title">{item.title}</h3>
                        )}

                        {/* Description */}
                        {item.description && (
                          <div className="nli-desc-container">
                            <p className="nli-card-desc">{item.description}</p>
                          </div>
                        )}

                        {/* Tags Row - Below Description */}
                        {itemTags.length > 0 && (
                          <div className="nli-card-tags-row">
                            {itemTags.map((tag, idx) => (
                              <span key={idx} className="nli-tag-chip">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Card Footer Actions */}
                        <div className="nli-card-footer">
                          <button
                            type="button"
                            className="nli-cta-btn"
                            aria-label={`${buttonText} - ${item.title || ""}`}
                          >
                            {isVideo && <FaPlay className="nli-btn-icon" />}
                            <span>{buttonText}</span>
                            <FaArrowRight className="nli-cta-arrow" />
                          </button>
                        </div>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          !newsletter_items_loading && (
            <div className="nli-empty-state">
              <FaRegFolderOpen className="nli-empty-icon" />
              <h3 className="nli-empty-title">
                No Content <span className="gradient-text">Available Yet</span>
              </h3>
            </div>
          )
        )}
      </div>

      {/* ==================== ULTRA-PREMIUM DETAIL MODAL ==================== */}
      <Modal
        isOpen={Boolean(selectedItem)}
        onRequestClose={() => setSelectedItem(null)}
        contentLabel={selectedItem?.title || "Item Detail"}
        className="nli-modal-card"
        overlayClassName="nli-modal-backdrop"
        closeTimeoutMS={250}
        shouldReturnFocusAfterClose={false}
        preventScroll={true}
      >
        {selectedItem && (
          <>
            {/* Floating Top Close Button */}
            <button
              type="button"
              className="nli-modal-close-btn"
              onClick={() => setSelectedItem(null)}
              title="Close"
              aria-label="Close"
            >
              <FaXmark />
            </button>

            {/* Modal Media Showcase */}
            <div className="nli-modal-media-box">
              {selectedItem.media_type === "video_link" && selectedItem.video_link ? (
                getEmbedUrl(selectedItem.video_link) ? (
                  <iframe
                    src={getEmbedUrl(selectedItem.video_link)}
                    title={selectedItem.title || "Video Player"}
                    className="nli-modal-video-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={
                      selectedItem.image_full_path ||
                      getYouTubeThumbnail(selectedItem.video_link) ||
                      No_img
                    }
                    alt={selectedItem.title || ""}
                    className="nli-modal-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = No_img;
                    }}
                  />
                )
              ) : (
                <img
                  src={selectedItem.image_full_path || No_img}
                  alt={selectedItem.title || ""}
                  className="nli-modal-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = No_img;
                  }}
                />
              )}
            </div>

            {/* Modal Content Body */}
            <div className="nli-modal-body">
              {/* Title */}
              {selectedItem.title && (
                <h2 className="nli-modal-title">{selectedItem.title}</h2>
              )}

              {/* Tags */}
              {parseTags(selectedItem.tags).length > 0 && (
                <div className="nli-modal-tags-row">
                  {parseTags(selectedItem.tags).map((tag, idx) => (
                    <span key={idx} className="nli-tag-chip modal-chip">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Subtle Divider */}
              <div className="nli-modal-divider" />

              {/* Full Description */}
              {selectedItem.description && (
                <div className="nli-modal-desc-wrapper">
                  <p className="nli-modal-desc">{selectedItem.description}</p>
                </div>
              )}

              {/* Modal Actions Footer */}
              <div className="nli-modal-footer">
                <button
                  type="button"
                  className="nli-modal-done-btn"
                  onClick={() => setSelectedItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default NewsLetterItemPage;
